import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Contact, Droplet } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Field, Input, Select } from '@/components/ui/primitives';
import { PhoneText } from '@/components/ui/phone-text';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { GROUP_LABEL } from './BloodBankPage';

const DONORS_TAB = '/blood-bank?tab=donors';
const EMPTY_FORM = { name: '', bloodGroup: 'o_pos', phone: '', cnic: '' };

export function RegisterDonorPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), DONORS_TAB);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const register = useMutation({
    mutationFn: () => post('/blood-bank/donors', { ...form, cnic: form.cnic || undefined, phone: form.phone || undefined }),
    onSuccess: () => {
      toast.success('Donor registered');
      void queryClient.invalidateQueries({ queryKey: ['donors'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const valid = form.name.trim().length >= 2;
  const dirty = !register.isSuccess && JSON.stringify(form) !== JSON.stringify(EMPTY_FORM);

  return (
    <FormScreen
      title="Register a donor"
      icon={Droplet}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        setError(null);
        register.mutate();
      }}
    >
      <FormSection title={tr('admin', 'identity')} icon={Droplet}>
        <Field label="Name" required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Blood group" required>
          <Select value={form.bloodGroup} onChange={(e) => setForm({ ...form, bloodGroup: e.target.value })}>
            {['o_pos', 'o_neg', 'a_pos', 'a_neg', 'b_pos', 'b_neg', 'ab_pos', 'ab_neg'].map((g) => (
              <option key={g} value={g}>
                {GROUP_LABEL(g)}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>

      <FormSection title={tr('admin', 'contact')} icon={Contact}>
        <Field label="Phone">
          <PhoneText value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>
        <Field label="CNIC">
          <Input value={form.cnic} onChange={(e) => setForm({ ...form, cnic: e.target.value })} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Register" loading={register.isPending} incomplete={!valid} cancelTo={returnTo} />
    </FormScreen>
  );
}
